import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";

import { Button, InputText, Text } from "@pentabd/ui";

import useActionCreator from "../../../../../../redux/hooks/useActionCreator";
import { CategoryCreateType } from "@type/inventory/category";
import { useEffect, useState } from "react";
import { useStatusById } from "@hooks/taskManagement/settings/status/useStatusById";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import { StatusCreateType } from "@type/taskManagement/settings/status";
import { ErrorModal, SuccessModal } from "@components";

interface InputTypes {
  name: string;
}

export default function StatusForm() {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<InputTypes>();

  const [id, setId] = useState<number | string>("");
  const [successSubtitle, setSuccessSubtitle] = useState<string>("");

  const { statusState } = useStatusById(false);
  const { getSingleData } = statusState;

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const {
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteSuccess,
    deleteError,
  } = useCustomSelector((store) => store.taskManagement.settingsStatusState);

  const { taskManagementActionsHandlers } = useActionCreator();

  const { statusCreateActionHandler, statusUpdateActionHandler } =
    taskManagementActionsHandlers;

  const createSuccessAction = () => {
    reset();
    setId("");
    setIsSuccessModalOpen(false);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onSubmit: SubmitHandler<InputTypes> = (data: StatusCreateType) => {
    if (id) {
      statusUpdateActionHandler(id, data);
    } else {
      statusCreateActionHandler(data);
    }
  };

  useEffect(() => {
    if (getSingleData) {
      setValue("name", getSingleData.name);
      setId(getSingleData.id);
    }
  }, [getSingleData]);

  useEffect(() => {
    if (createSuccess) {
      setSuccessSubtitle("You have created a task settings status");
      setIsSuccessModalOpen(true);
    }

    if (updateSuccess) {
      setSuccessSubtitle("You have updated a task settings status");
      setIsSuccessModalOpen(true);
    }

    if (deleteSuccess) {
      setSuccessSubtitle("You have deleted a task settings status");
      setIsSuccessModalOpen(true);
    }

    if (createError || updateError || deleteError) {
      setSuccessSubtitle("");
      setIsErrorModalOpen(true);
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteSuccess,
    deleteError,
  ]);

  return (
    <>
      <div className="bg-primary-lightest rounded p-12 my-12">
        <div className="pb-8">
          <Text color="#101828" component="h2" size="xl" weight="semibold">
            {id
              ? t("TM_SETTINGS.STATUS.FORM.LABEL_UPDATE")
              : t("TM_SETTINGS.STATUS.FORM.LABEL")}
          </Text>
        </div>

        <div className="pt-8 border-top">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-12 col-lg-4">
                <Text
                  color="#344054"
                  component="p"
                  size="md"
                  weight="semibold"
                  className="mb-6"
                >
                  {t("TM_SETTINGS.STATUS.FORM.FIELDS.FIELD_1.NAME")}
                </Text>

                <InputText
                  {...register("name", {
                    required: "Status name is required",
                  })}
                  placeholder={t(
                    "TM_SETTINGS.STATUS.FORM.FIELDS.FIELD_1.PLACEHOLDER",
                  )}
                  controlling
                />

                {errors?.name && (
                  <Text
                    color="danger"
                    component="p"
                    size="xs"
                    weight="lighter"
                    className="mt-2"
                  >
                    {errors?.name?.message || ""}
                  </Text>
                )}
              </div>
              <div className="col-12 col-lg-4">
                <Text
                  color="#344054"
                  component="p"
                  size="md"
                  weight="semibold"
                  className="mb-0 mb-lg-6 d-none d-lg-block invisible"
                >
                  Hidden Text
                </Text>
                <Button
                  fill="fill"
                  size="md"
                  type="info"
                  htmlType="submit"
                  isPadding={false}
                  className="px-20 py-5"
                >
                  {id
                    ? t("TM_SETTINGS.STATUS.FORM.BUTTON.UPDATE_BUTTON")
                    : t("TM_SETTINGS.STATUS.FORM.BUTTON.ADD_BUTTON")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
          subtitle={successSubtitle}
        />
      )}

      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
    </>
  );
}
